import loadable from '@loadable/component';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { render } from 'react-dom';
import { Provider as ReduxProvider, useDispatch, useSelector } from 'react-redux';

import configureStore from 'store';

import { getCurrency, getDictionary, getLanguage, getMarket } from '@travel/i18n';
import { Close } from '@travel/icons/ui';
import {
  providerListSearchFormHeight,
  providerListSectionMarginTop,
  travelerHeaderHeightPC,
} from '@travel/traveler-core/constants/styles';
import {
  DESKTOP_SCREEN,
  useCurrencyChangeCallback,
  useDeviceType,
} from '@travel/traveler-core/hooks';
import { toSEOSearchString } from '@travel/traveler-core/utils';
import { Tag } from '@travel/ui';
import { cx, isEmptyArray, isNotEmptyArray } from '@travel/utils';

import CouponAccordion from 'components/CouponAccordion';
import Dialog from 'components/Dialog';
import { FilterItem } from 'components/ProviderFilterTags';
import ProviderSummary from 'components/ProviderSummary';
import SortSelectBox from 'components/SortSelectBox';
import WithSkeletonLoading from 'components/WithSkeletonLoading';
import { STAR_RATING_KEY } from 'pages/ProviderListPage/constants';

import { pushLocation, replaceLocation } from 'store/__router/actions';
import { getLocation } from 'store/__router/selectors';
import { getHeight } from 'store/banner/selectors';
import { getIsGlobalCurrency } from 'store/markets/selectors';
import { getIsFetchingList, getItems, getPlaceName } from 'store/providerList/selectors';
import { getCouponDetails } from 'store/salesPromotion/selectors';

import { PROVIDER_CONTRACT_TYPES } from 'constants/provider';
import { OFFER_TYPE } from 'constants/salesPromotion';
import { Translate } from 'core/translate';
import paths from 'core/universalRouter/paths';
import type { ProviderItem, SearchFormQuery } from 'ProviderList-Types';

import type { FilterFormProps } from '../FilterForm';
import type { FilterListProps } from '../FilterList';
import Map from '../Map';
import { createPopupClass } from '../Map/MapCard';
import { useMapContext } from '../Map/mapContext';
import ProviderList from '../ProviderList';
import MapButton from './MapButton';

import styles from './filtersAndList.module.scss';

const FilterList = loadable(
  () =>
    import(
      /* webpackMode: "lazy" */
      /* webpackFetchPriority: "low" */
      /* webpackChunkName: "component-FilterList" */
      '../FilterList'
    ),
  { ssr: false },
);

type Props = {
  /** Object of current page params */
  params: { pageIdentifier: string };
  /** Object of current page query */
  query: SearchFormQuery;
  /** Callback to be called when list style updated */
  onUpdateListView: () => void;
  /** Flag to define whether the map is expanded or not */
  isExpandedMap: boolean;
  /** Callback to be called when remove button of coupon name is clicked */
  onRemoveCoupon?: () => void;
  /** Filter search form values for PC/TL */
  filterFormProps: FilterFormProps;
  /** Filter search form values for PC/TL */
  filtersInListProps: FilterListProps;
  newQuery?: SearchFormQuery;
};

type MapPinStoreType = {
  [id: string]: any; // TODO: Put MarkerWithLabel type
};

function FiltersAndList(props: Props) {
  const { params, query, onUpdateListView, isExpandedMap, newQuery } = props;
  const dispatch = useDispatch();
  const deviceType = useDeviceType();
  const isFetchingList = useSelector(getIsFetchingList);
  const location = useSelector(getLocation);
  const currency = useSelector(getCurrency);
  const language = useSelector(getLanguage);
  const market = useSelector(getMarket);
  const isGlobalCurrency = useSelector(getIsGlobalCurrency);
  const areaName = useSelector(getPlaceName);
  const couponDetail = useSelector(getCouponDetails(query.salesPromotionId));
  const { providers, total } = useSelector(getItems);
  const bannerHeight = useSelector(getHeight);
  const { resetValues } = useMapContext();
  const top =
    bannerHeight +
    travelerHeaderHeightPC +
    providerListSearchFormHeight +
    providerListSectionMarginTop;

  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const markersRef = useRef<MapPinStoreType>({});
  const mapRef = useRef<google.maps.Map | null>(null);
  const prevMarkerRef = useRef<google.maps.Marker | null>(null);
  const setMapInstance = useCallback(mapObj => (mapRef.current = mapObj), []);

  // should use contract type of list as faceting is client call
  const contractType = providers?.length > 0 ? providers[0]?.contractType : '';
  const isDirectContract = contractType === PROVIDER_CONTRACT_TYPES.DIRECT;

  const isPC = deviceType === DESKTOP_SCREEN;

  const showTotalCount = isNotEmptyArray(providers) && !query?.salesPromotionId && isDirectContract;

  /* Id of previously clicked item id */
  const prevItemIdRef = useRef(selectedItemId);
  const setSelectedItemWithRef = useCallback((id: string | null) => {
    prevItemIdRef.current = id;
    setSelectedItemId(id);
  }, []);

  /* Reset all map pins */
  const resetMarkers = useCallback(() => {
    Object.values(markersRef.current).map(marker => marker.setMap(null));
    markersRef.current = {};
  }, []);

  /* Reset selected map pin by id */
  const resetMapPin = useCallback((id: string) => {
    if (markersRef.current?.[id]) {
      markersRef.current[id].setMap(null);
      delete markersRef.current[id];
    }
  }, []);

  //TODO: will hoist this function to page level (TIDP-47274)
  /* Currency change handler */
  const onCurrencyChange = useCallback(() => {
    resetMarkers();

    // To deal with array, we process starRating separately
    const { [STAR_RATING_KEY]: starRatingValues, ...restQuery } = query;
    const searchParams = new URLSearchParams(
      toSEOSearchString({ ...restQuery, maxPrice: '', minPrice: '' }),
    );
    starRatingValues !== undefined &&
      (Array.isArray(starRatingValues)
        ? starRatingValues?.map(value => searchParams.append(STAR_RATING_KEY, value))
        : searchParams.append(STAR_RATING_KEY, starRatingValues));

    // When we change currency, we initialize min-max value
    dispatch(
      replaceLocation({
        pathname: paths.providerList.pathResolver(params.pageIdentifier),
        search: searchParams.toString(),
      }),
    );
  }, [dispatch, resetMarkers, query, params.pageIdentifier]);
  useCurrencyChangeCallback(onCurrencyChange);

  useEffect(() => {
    if (selectedItemId) {
      markersRef.current[selectedItemId]?.set('labelClass', styles.labelClicked);
    }
    // add style for selected item when re-render
     
  }, [selectedItemId, isExpandedMap]);

  const resetPrevPinStyle = () => {
    const prevPin = markersRef.current[prevItemIdRef.current || ''];
    if (prevItemIdRef.current && prevPin) {
      prevPin.set('labelClass', styles.label);
    }
  };

  const onChangeMapPinStyle = useCallback(
    (ItemId: string) => {
      if (prevItemIdRef.current !== ItemId) {
        resetPrevPinStyle();
        setSelectedItemWithRef(ItemId);
      }
    },
    [setSelectedItemWithRef],
  );

  const onMouseOver = useCallback((ItemId: string) => onChangeMapPinStyle(ItemId), [
    onChangeMapPinStyle,
  ]);

  const onProviderSummaryClick = useCallback(
    (item: ProviderItem) => {
      dispatch(
        pushLocation({
          pathname: paths.providerInfo.pathResolver(item.pathId),
          search: location.search,
        }),
      );
    },
    [dispatch, location.search],
  );

  const dictionary = useSelector(getDictionary);

  const createProviderSummary = useCallback(
    (item: ProviderItem) => {
      const medium = item.media?.find(medium => !!medium.url);
      const store = configureStore({ _i18n: { dictionary } });
      const isDirectContract = item.contractType === PROVIDER_CONTRACT_TYPES.DIRECT;

      return (
        <ReduxProvider store={store}>
          <ProviderSummary
            className={styles.providerSummary}
            id={item.id}
            price={item.price}
            name={item.name}
            reviewScore={item.reviews.score}
            totalReviews={item.reviews.count}
            reviewLabel={item.reviews.label}
            media={medium}
            starRating={item.starRating}
            onClick={() => onProviderSummaryClick(item)}
            isExpandedMap={isExpandedMap}
            language={language}
            currency={currency}
            market={market.marketCode}
            isGlobalCurrency={isGlobalCurrency}
            isDirectContract={isDirectContract}
          />
        </ReduxProvider>
      );
    },
    [
      currency,
      isGlobalCurrency,
      language,
      market.marketCode,
      onProviderSummaryClick,
      isExpandedMap,
      dictionary,
    ],
  );

  // Below function is mixed up with creating mapCard and manage clicked mapCard state
  const createMapCard = useCallback(
    (map, mapPin, item) => {
      // Google API can take only DOM Node, not ReactNode
      const cardWrapper = document.createElement('div');
      if (typeof item === 'string') {
        render(
          <Tag
            className={cx(styles.mapLabel, styles.labelAnchor)}
            type={'filled'}
            color={'white'}
            data-testid="listAndMap-searchedMapPin-tag"
          >
            {areaName}
          </Tag>,
          cardWrapper,
        );
      } else {
        render(createProviderSummary(item), cardWrapper);
      }

      const Popup = createPopupClass();
      const position = mapPin.getPosition();
      // @ts-ignore
      const mapCard = new Popup(position, cardWrapper);

      if (prevMarkerRef.current) {
        prevMarkerRef.current?.setMap(null); // Close previously open mapCard
      }

      prevMarkerRef.current = mapCard; // Remember created mapCard
      mapCard.setMap(map); // Display mapCard on the map by setting map property of mapCard
    },
    [areaName, createProviderSummary],
  );

  const onMapPinClick = useCallback(
    (item: ProviderItem) => {
      onChangeMapPinStyle(item.id);

      if (!!mapRef.current && markersRef.current[item.id]) {
        createMapCard(mapRef.current, markersRef.current[item.id], item);
      }
    },
    [createMapCard, onChangeMapPinStyle],
  );

  const onSearchedSpotMapPinClick = useCallback(
    (itemId: string) => {
      createMapCard(mapRef.current, markersRef.current[itemId], itemId);
      // reset the style for prevPin
      resetPrevPinStyle();
    },
    [createMapCard],
  );

  const initializePinAndCard = useCallback(() => {
    prevMarkerRef.current?.setMap(null);
    prevMarkerRef.current = null;

    // If prevMapPin exists, change its style as unselected
    if (prevItemIdRef.current) {
      const prevPin = markersRef.current[prevItemIdRef.current || ''];
      if (prevPin) {
        prevPin.set('labelClass', styles.label);
      }
      setSelectedItemWithRef(null);
    }
  }, [setSelectedItemWithRef]);

  const storeMapPinInListAndMap = useCallback((id: string, mapPinObj: google.maps.Marker) => {
    markersRef.current = { ...markersRef.current, [id]: mapPinObj };
  }, []);

  const onOpenCloseMap = () => {
    resetValues();
    onUpdateListView();
  };

  return (
    <>
      <div
        className={cx(styles.wrapper, styles.expandedListView)}
        data-testid={`filtersAndList-wrapper-list`}
      >
        {/*  Filters and map button */}
        <div className={styles.filterInListWrapper} style={{ top: top }}>
          <MapButton onOpenMap={onOpenCloseMap} />
          <FilterList
            {...props.filtersInListProps}
            className={styles.filterInListAndBtn}
            filterFormProps={props.filterFormProps}
            isLoading={isFetchingList}
          />
        </div>

        {/*  List */}
        <div className={cx(styles.providerListWrapper, 'thin-scrollbar')}>
          <div className={styles.providerListButtons}>
            <SortSelectBox
              className={styles.sortBox}
              query={query}
              params={params}
              newQuery={newQuery}
            />
            {query.salesPromotionId && couponDetail?.offerType !== OFFER_TYPE.DIRECT && (
              <FilterItem
                key={'selected-couponDetail'}
                title={couponDetail?.representativeFeatureName}
                onRemove={props.onRemoveCoupon}
              />
            )}
          </div>
          {query.salesPromotionId && couponDetail?.offerType !== OFFER_TYPE.DIRECT && (
            <CouponAccordion
              className={styles.couponAccordion}
              couponId={query.salesPromotionId}
              onRemoveCoupon={props.onRemoveCoupon}
            />
          )}
          <WithSkeletonLoading
            isLoading={isFetchingList}
            count={3}
            row={3}
            length="long"
            customStyle="textWithImageRow"
            skeletonItemClassName={styles.loader}
            skeletonImageClassName={styles.loaderImage}
          >
            {showTotalCount ? (
              <Translate
                className={styles.totalProperties}
                id="Provider_List.Pagination.Hotel.Totals"
                data={{ num_properties: total }}
                count={total}
              />
            ) : null}
            <ProviderList
              query={query}
              params={params}
              onMouseOver={isPC ? onMouseOver : undefined}
              onRemoveCoupon={props.onRemoveCoupon}
            />
          </WithSkeletonLoading>
        </div>
      </div>
      {/* map Dialog */}
      {isExpandedMap && (
        <Dialog
          className={styles.dialogClassName}
          contentClassName={styles.dialogContent}
          onClose={onOpenCloseMap}
          isOpen={isExpandedMap}
          shouldCloseOnEscPress={true}
          hasAnimation={true}
        >
          <button
            className={styles.closeButton}
            onClick={onOpenCloseMap}
            aria-label="Close map"
            data-testid="filtersAndList-mapViewClose-button"
          >
            <Close size={22} />
          </button>
          <div
            className={cx(styles.wrapper, styles.mapView)}
            data-testid={`filtersAndList-mapDialog-wrapper`}
          >
            <div className={styles.filterInListWrapper} style={{ top: top }}>
              <FilterList
                {...props.filtersInListProps}
                className={styles.filterInListAndBtnDialog}
                filterFormProps={props.filterFormProps}
                isLoading={isFetchingList}
              />
            </div>

            <div className={styles.buttonsAndListAndMap}>
              <div className={styles.providerListButtons}>
                <SortSelectBox
                  className={styles.sortBox}
                  query={query}
                  params={params}
                  newQuery={newQuery}
                />
                {query.salesPromotionId && couponDetail?.offerType !== OFFER_TYPE.DIRECT && (
                  <FilterItem
                    key={'selected-couponDetail'}
                    title={couponDetail?.representativeFeatureName}
                    onRemove={props.onRemoveCoupon}
                  />
                )}
              </div>
              {/*  List */}
              <div className={styles.listAndMap}>
                <div
                  className={cx(
                    styles.providerListWrapper,
                    'thin-scrollbar',
                    isEmptyArray(providers) && styles.emptyProviderList,
                  )}
                >
                  {query.salesPromotionId && couponDetail?.offerType !== OFFER_TYPE.DIRECT && (
                    <CouponAccordion
                      isSmallList={true}
                      className={styles.couponAccordion}
                      couponId={query.salesPromotionId}
                      onRemoveCoupon={props.onRemoveCoupon}
                    />
                  )}
                  <WithSkeletonLoading
                    isLoading={isFetchingList}
                    count={3}
                    row={3}
                    length="long"
                    customStyle="textWithImageRow"
                    skeletonItemClassName={styles.loader}
                    skeletonImageClassName={styles.loaderImage}
                  >
                    <ProviderList
                      query={query}
                      params={params}
                      onMouseOver={isPC ? onMouseOver : undefined}
                      isExpandedMap={isExpandedMap}
                      onRemoveCoupon={props.onRemoveCoupon}
                    />
                  </WithSkeletonLoading>
                </div>
                <div className={styles.mapWrapper}>
                  <WithSkeletonLoading
                    isLoading={isFetchingList}
                    count={1}
                    row={1}
                    length="long"
                    customStyle="image"
                    className={styles.mapLoader}
                    skeletonItemClassName={styles.mapLoaderItem}
                  >
                    <Map
                      isInDialog={true}
                      className={styles.mapClassName}
                      params={props.params}
                      query={props.query}
                      initializePinAndCard={initializePinAndCard}
                      storeMapPinInListAndMap={storeMapPinInListAndMap}
                      onMapPinClick={onMapPinClick}
                      onSearchedSpotMapPinClick={onSearchedSpotMapPinClick}
                      setMapInstance={setMapInstance}
                      language={language}
                      currency={currency}
                      isGlobalCurrency={isGlobalCurrency}
                      onResetMapPins={resetMarkers}
                      resetMapPin={resetMapPin}
                    />
                  </WithSkeletonLoading>
                </div>
              </div>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}

export default React.memo(FiltersAndList);
